import React from 'react';
import { Row, Col, Form } from "react-bootstrap";
import "../../sass/main.scss";
import './FormField.scss';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core';
import { INVALID_DATE } from '../constants/ApiConstants';
import { mirrorData } from '../../common/utils/appUtils'
import { isCompleteCalendarDate } from '../../components/dataEntryDetails/dataEntryComponents/quickEntryFieldConstraints'

export interface DateFieldProps {
    label?: string,
    placeholder: string,
    required?: boolean,
    maxDate?: Date,
    minDate?: Date,
    errMsg: string,
    onChange: (value?: Date | string) => void,
    value?: Date | string,
    showError?: boolean,
    disabled?: boolean,
    className?: string,
    name?: string,
    onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    /**
     * AB#38460: text the specialist has typed that is not a usable date, owned by the CALLER.
     *
     * Passed straight to the picker's `inputValue`, which takes controlled ownership of the
     * displayed text (`inputValue || innerInputValue` in useKeyboardPickerState) - that is what
     * keeps a bad entry on screen instead of the box blanking. Caller-owned rather than local
     * state so the caller can clear it on Cancel/Confirm; local state could not be reset from
     * outside, because the picker only re-syncs when `value` changes and an invalid entry
     * deliberately leaves `value` alone.
     */
    invalidText?: string,
    /**
     * AB#38460 (John James, PR !10140 review): bump this to force the picker to forget the text it
     * is displaying.
     *
     * useKeyboardPickerState resets its internal text from an effect keyed on [displayDate, value].
     * REMOVING a previously-supplied `inputValue` is not in those deps, and the invalid path
     * deliberately never touches `value` - so clearing invalidText leaves the bad text on screen
     * with nothing to trigger a re-sync. Usually invisible, because a row that returns to Confirmed
     * unmounts this component and takes the state with it. It bites on a row that stays mounted and
     * confirmable: Due Date is EmptyNotRequired, so after Cancel the box still showed 99/99/9999
     * while Confirm was enabled.
     *
     * Changing it remounts the picker, which is the only thing that clears that state. Bump it when
     * a row LEAVES EDIT MODE, never while typing - a remount mid-entry would drop focus and undo
     * the backspace and text-retention behaviour this branch already fixed.
     */
    resyncKey?: string | number,
    /**
     * AB#38460: reports text that is not a usable date, WITHOUT touching `onChange`.
     *
     * Separate from onChange on purpose. Reporting an unusable entry as a VALUE is what used to
     * blank the field: the sentinel became the value, the value drove the picker, and the text
     * vanished mid-edit. This channel lets the caller show an error and block Confirm while the
     * stored value - and the text on screen - stay exactly as they were. Called with undefined
     * as soon as the entry becomes usable or empty.
     *
     * `committed` is true only on BLUR. Callers use it to decide whether to show a message yet: an
     * entry still being typed must not be nagged about, but one left half-finished on blur must be
     * - otherwise the row goes red with nothing explaining why, which is AB#38460 all over again.
     */
    onInvalidText?: (text?: string, committed?: boolean) => void,
    onChangeVal?: (value?: Date, name?: string) => void,
    onChangeRTRDateRange?: (name: string | undefined, value: string) => void,
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void,
    labelSameLine?: boolean
}

function DateField(props: DateFieldProps) {

    const {
        label,
        name,
        placeholder,
        required,
        maxDate,
        minDate,
        errMsg = "",
        onChange,
        value,
        onKeyDown
    } = props


    const useStyle = makeStyles(theme => ({
        root: {
            '& .MuiInput-root': {
                border: "1px solid #CC5967 !important"
            }
        }

    }))


    const classes = useStyle()

    /**
     * AB#38460: the picker value, stable by IDENTITY across renders.
     *
     * useKeyboardPickerState resets the text it is displaying from an effect whose dependency
     * list includes the `value` object itself. Several callers build that value fresh on every
     * render (toDisplayDate returns `new Date(...)` for an AI-seeded UTC-midnight value), so the
     * identity changed on every render even when the date had not - the effect re-ran and reset
     * the box to the stored value. Any re-render that happened while the specialist was typing
     * therefore threw their edit away: backspacing a digit looked like the key did nothing.
     *
     * Memoised on the instant rather than the object, so a caller passing an equal-but-new Date
     * no longer counts as a change. Fixes it for every DateField caller at once instead of asking
     * each of them to hold their value stable.
     */
    const valueKey = value instanceof Date ? value.getTime() : value
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stableValue = React.useMemo(() => value, [valueKey])

    const onChangeDate = (date?: Date, text?: string) => {
        props.onChangeVal?.(date, props.name);

        if (date == undefined) {
            props.onInvalidText?.(undefined)
            onChange(undefined)
        } else if (!isNaN(date.getTime())) {
            props.onInvalidText?.(undefined)
            onChange(date)
            props.onChangeRTRDateRange?.(props.name, date.toISOString())
            mirrorData(name ?? "", date)
        } else {
            // AB#38460: anything else in the box is not a usable date - either complete and
            // unreal ("99/99/9999") or unfinished ("99/99/999", mid-backspace). BOTH are reported
            // so neither can be confirmed; the caller decides which of them also warrants a
            // visible message, since nagging mid-keystroke is not the same as refusing to save.
            //
            // Reported through onInvalidText, never onChange: routing it through the value is what
            // used to blank the box mid-edit. The value stays untouched, so nothing unusable can
            // be stored and the text the specialist typed stays on screen.
            //
            // A masked placeholder is normalised away first - the picker pads an unfinished entry
            // to "12/31/202_", and the trailing underscores are the mask's, not the specialist's.
            const typed = (text ?? "").replace(/_/g, "")
            props.onInvalidText?.(typed === "" ? undefined : typed)
        }
        // An Invalid Date deliberately calls NOTHING here. This fires on every KEYSTROKE, and a
        // date is unparseable for most of the time it is being typed - backspacing one digit off
        // "12/31/2023" leaves "12/31/202_", which parses to Invalid Date. Reporting that would
        // push the sentinel to the consumer, which clears the value, which resets this picker's
        // displayed text - so a single backspace wiped the whole field mid-edit (AB#38460, second
        // report). Validation belongs on blur, once the specialist has finished: onBlur below
        // catches every unusable class, including the rollover dates new Date() misses.
    }

    const onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.target.value === ""){
            props.onInvalidText?.(undefined);
            onChange(undefined);
            return;
        }

        // AB#38460: `new Date(text)` cannot decide this. It is not NaN for a rolled-over date -
        // new Date("02/30/1996") quietly becomes 1996-03-01 - so "02/30/1996", "04/31/2000" and
        // "02/29/1997" (a non-leap year, a common mis-transcription off a faxed authorization)
        // all slipped through as valid, and the box kept showing a filled-in date with no error.
        // isCompleteCalendarDate round-trips the parsed parts, so a rollover fails, and its shape
        // regex also rejects a half-typed masked entry ("10/22/19__"). One predicate, every class
        // this handler can see. Reused from AB#37980 rather than re-derived - it was written for
        // exactly this defect and is already unit-tested.
        if (!isCompleteCalendarDate(e.target.value)) {
            // AB#38460. Callers that opt into onInvalidText get the entry reported as TEXT, which
            // leaves their value untouched and keeps the text on screen (see that prop's
            // docblock). This also catches an entry left half-finished, which the keystroke path
            // deliberately stays quiet on.
            //
            // The new channel is OPT-IN, and is currently wired only in Quick Entry (the six
            // DataEntryDetails.tsx call sites). The other ~12 <DateField> callers - only one of
            // which is a DOB field - pass neither prop and keep the original contract, the
            // sentinel through onChange. They render their own error from that value, so
            // switching them to a channel they do not listen on would silently remove their
            // validation entirely.
            //
            // The consequence worth stating rather than discovering: Classic's Authorization and
            // Request Date (DataEntryDetailsRightPane.tsx) are among those callers, so a rollover
            // date typed there still routes through the sentinel and BLANKS the box - the very
            // behaviour AB#38460 fixed in Quick Entry. Deliberate scope boundary, not an
            // oversight: Classic Mode is out of scope for this card (John James, PR !10140
            // review). The four wrapper components already accept and forward both props, so
            // wiring Classic later is a call-site change only.
            if (props.onInvalidText) {
                // AB#38460: strip the mask placeholder exactly as the keystroke path does, so the
                // SAME entry does not store "10/22/19" when typed and "10/22/19__" when blurred.
                // The stored text is echoed back as inputValue and compared for equality before
                // re-rendering, so the two spellings caused a needless render on every blur.
                //
                // committed = true: blur means the specialist has finished, so the caller may show
                // a message even for a half-finished entry - which the keystroke path deliberately
                // stays quiet about (David Heifetz, PR !10140 review).
                props.onInvalidText(e.target.value.replace(/_/g, ""), true)
            } else {
                onChange(INVALID_DATE)
            }
            return;
        }

        props.onInvalidText?.(undefined)
        let date = new Date(e.target.value)

        if (isNaN(date.valueOf())) {
            onChange(INVALID_DATE)
        } else if (props.onBlur) {
            props.onBlur(e);
        } else if (name) {
            let patientMinDOB = new Date();
            patientMinDOB.setFullYear(patientMinDOB.getFullYear() - 120);
            let futureDate = new Date();
            if (date.getTime() > futureDate.getTime()) {
            } else if (date.getTime() < patientMinDOB.getTime()) {
            } else if (date && !isNaN(date.getTime())) {
                mirrorData(name, date)
            } else {
                mirrorData(name, null)
            }
        }
    }

    const onKeyup = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "T" || e.key === "t") {
            var d = new Date();
            d.setHours(0);
            d.setMinutes(0);
            d.setSeconds(0);
            d.setMilliseconds(0);
            onChangeDate(d);
        }
    }

    const labelBlock = () => {
        return <Form.Label >{label}{required ?
            <span className="required" style={{ color: "red" }}>*</span> : null}</Form.Label>
    }

    const pickerBlock = () => {
        return (<>
                {errMsg.length > 0 ? <span className="alert-icon  alert-icon-align"></span> : ''}
                <MuiPickersUtilsProvider utils={DateFnsUtils}  >
                    <KeyboardDatePicker
                        key={props.resyncKey}
                        className={errMsg.length > 0 ? classes.root : props.disabled ? 'date-field-disable date-field-style' : 'date-field-style'}
                        onBlur={onBlur}
                        fullWidth
                        variant="inline"
                        format="MM/dd/yyyy"
                        margin="normal"
                        id="date-picker-inline"
                        value={stableValue && stableValue.toString() != INVALID_DATE ? stableValue : null}
                        KeyboardButtonProps={{
                            'aria-label': 'change date',
                        }}
                        allowKeyboardControl={true}
                        autoOk={true}
                        maxDate={maxDate}
                        minDate={minDate}
                        placeholder={placeholder}
                        {...(errMsg && { error: true, helperText: errMsg })}
                        type="text"
                        // AB#38460: takes controlled ownership of the displayed text while the
                        // entry is unusable, so it stays on screen. Undefined otherwise, which
                        // hands the picker straight back to its own internal state.
                        {...(props.invalidText !== undefined ? { inputValue: props.invalidText } : {})}
                        // The second argument is the RAW TEXT, which is the only way to tell a
                        // complete-but-unreal date from one still being typed.
                        onChange={(date, text) => onChangeDate(date ?? undefined, text ?? undefined)}
                        onKeyUp={key => onKeyup(key)}
                        onKeyDown={e => onKeyDown && onKeyDown(e)}
                        disabled={props.disabled}
                    />
                </MuiPickersUtilsProvider>
            </>)
    }

    const errorBlock = () => {
        return (<span className="error-message">{props.errMsg}</span>)
    }

    const labelSameLine = () => {
        return (<Form className={`date-field-input ${props.className}`} onSubmit={e => { e.preventDefault(); }}>
            <Row>
                <Col>{label ? 
                 labelBlock() 
             : null}
                { pickerBlock() }
                </Col>
            </Row>
            {props.errMsg.length > 0 ?
                <Row>
                    <Col>{ errorBlock() }</Col>
                </Row> : null}
        </Form>)
    }

    const labelSeparateLine = () => {
        return (<Form className={`date-field-input ${props.className}`} onSubmit={e => { e.preventDefault(); }}>
            {label ? <Row>
                <Col>{ labelBlock() }</Col>
            </Row> : null}
            <Row>
                <Col>{ pickerBlock() }</Col>
            </Row>
            {props.errMsg.length > 0 ?
                <Row>
                    <Col >{ errorBlock() }</Col>
                </Row> : null}
        </Form>)
    }

    return (
        props.labelSameLine ? labelSameLine() : labelSeparateLine()
    )
}

export default DateField;
